// CRITICAL: Import layer order declaration FIRST before any other CSS
import "./main.css";
import { AnalyticsProvider } from "@analytics/client";
import { ErrorBoundary } from "@error-tracking/client";
import { loadRemote } from "@module-federation/enhanced/runtime";
import {
  AuthProvider,
  ImpersonationProvider,
  getActiveWorkspaceId,
  getKeycloakInstance,
} from "@spiriidk/auth";
import {
  FEATURE_FLAGS_CONFIG,
  DEFAULT_LOCALE,
  getStoredLocale,
  normalizeLocale,
  LOCALE_STORAGE_KEY,
} from "@spiriidk/connect-configs";
import {
  FeatureFlagProvider,
  initializeFeatureFlags,
  setContext,
} from "@spiriidk/feature-flags";
import { useLocalStorageSync } from "@spiriidk/utils";
import { QueryClientProvider } from "@tanstack/react-query";
import React, { StrictMode, Suspense, use, useEffect } from "react";
import { createRoot } from "react-dom/client";
import {
  addErrorHandler,
  getAppStatus,
  LOAD_ERROR,
  registerApplication,
  start,
} from "single-spa";

import { isConnectRoute, isTechUiRoute } from "~/utils/routes";

import { App } from "./app";
import { AppErrorFallback, FullPageLoader } from "./components";
import {
  analyticsClient,
  initializeAuth,
  initializeRefiner,
  isMixpanelEnabled,
  loadCatalog,
  loadParcel,
  queryClient,
  startErrorTracking,
  trackFlagEvaluation,
} from "./configs";
import { BetaFeaturesProvider } from "./features/navigation/contexts";
import { useEffectOnce } from "./hooks";
import { migrateBetaFeaturesStorageKey } from "./migrate-beta-features-storage-key";
import type { mockHandlerRegistry } from "./mocks";

interface InitializationData {
  authData: Awaited<ReturnType<typeof initializeAuth>>;
}

let initializationPromise: Promise<InitializationData> | null = null;

function getInitializationPromise(): Promise<InitializationData> {
  if (!initializationPromise) {
    initializationPromise = initializeApp();
  }

  return initializationPromise;
}

async function initializeApp(): Promise<InitializationData> {
  const isMockApiEnabled = import.meta.env.VITE_MOCK_API === "true";

  startErrorTracking();

  migrateBetaFeaturesStorageKey();

  const initialLocale = getStoredLocale();

  // Set initial HTML lang attribute
  document.documentElement.lang = initialLocale;

  await loadCatalog(initialLocale);

  // Initialize MSW early, before registering micro-frontends
  const mocks = isMockApiEnabled ? await import("./mocks") : undefined;

  await mocks?.enableMocking().catch((error) => {
    console.error("Failed to enable mocking:", error);
  });

  // Mock flag on: boot into an authenticated session as the test user through
  // the shared setKeycloakInstance seam, skipping every Keycloak network call.
  // A Playwright project/test can select the seeded user's permission set and
  // company/operator scope via a well-known localStorage key (e2e auth-scope
  // seam, design D5); without an override this resolves to the full-access,
  // company-scoped default. Flag off: the real check-sso / login flow is
  // unchanged (the seam is never consulted).
  const authData = mocks
    ? mocks.setupMockAuth(
        mocks.resolveMockAuthProfile({ storage: localStorage })
      )
    : await initializeAuth();

  await analyticsClient.identify(authData.login, {
    role: authData.userPermissions[0],
    operatorId: authData.permittedOperatorId,
    companyId: authData.permittedCompanyId,
    locale: initialLocale,
  });

  // Register identity as super properties so they attach to every event
  // (not just the user profile) for segmentation across all reports. `locale`
  // is also refreshed on language switch (see AppInitializer).
  if (isMixpanelEnabled) {
    analyticsClient.plugins.mixpanel.setSuperProperties({
      role: authData.userPermissions[0],
      operatorId: authData.permittedOperatorId,
      companyId: authData.permittedCompanyId,
      locale: initialLocale,
    });
  }

  await initializeFeatureFlags({
    // Under the mock flag, force the in-memory provider (FEATURE_FLAGS_CONFIG
    // defaults) so boot stays fast and hermetic — a real client id would open a
    // streaming connection to LaunchDarkly that msw cannot intercept.
    clientSideId: isMockApiEnabled
      ? undefined
      : (import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID as string | undefined),
    // Under the mock flag, a Playwright project can flip a flag's defaultVariant
    // via a well-known localStorage key (e2e flag-override seam, design D5); in a
    // real deployment this merge is inert and the config defaults pass through.
    flags: mocks
      ? mocks.resolveMockFlagConfig({
          isMockApiEnabled,
          config: FEATURE_FLAGS_CONFIG,
          storage: localStorage,
        })
      : FEATURE_FLAGS_CONFIG,
    options: {
      streaming: true,
    },
    // Forward every flag evaluation to Mixpanel (super property + $experiment_started).
    onEvaluation: trackFlagEvaluation,
  });

  await setContext({
    targetingKey: authData.userId,
    email: authData.email,
    username: authData.login,
    name: authData.name,
    groups: authData.groups,
    operatorId: authData.permittedOperatorId,
    companyId: authData.permittedCompanyId,
    permissions: authData.userPermissions,
  });

  initializeRefiner(authData, initialLocale);

  addErrorHandler((err) => {
    if (getAppStatus(err.appOrParcelName) === LOAD_ERROR) {
      console.error(
        "Failed to load micro-frontend:",
        err.appOrParcelName,
        err.message
      );
    }
  });

  // Register connect app's mock handlers if mocking is enabled
  if (mocks) {
    try {
      interface ConnectMockModule {
        registerMockHandlers?: () => Promise<
          ((registry: typeof mockHandlerRegistry) => void) | undefined
        >;
      }

      const connectApp =
        await loadRemote<ConnectMockModule>("spirii_connect/spa");

      if (connectApp?.registerMockHandlers) {
        const registerFn = await connectApp.registerMockHandlers();
        if (registerFn) {
          registerFn(mocks.mockHandlerRegistry);

          // Re-apply handlers to the running worker
          mocks.worker.use(...mocks.mockHandlerRegistry.getHandlers());
        }
      }
    } catch (error) {
      console.error("Failed to register connect mock handlers:", error);
    }
  }

  registerApplication(
    "@spiriidk/connect",
    () =>
      loadRemote("spirii_connect/spa") as Promise<
        import("single-spa").LifeCycles
      >,
    (location) => isConnectRoute(location.pathname),
    { kcInstance: getKeycloakInstance() }
  );

  // tech-ui is now an MF remote — load via Module Federation
  registerApplication(
    "@spiriidk/tech-ui",
    () =>
      loadRemote("spirii_tech_ui/spa") as Promise<
        import("single-spa").LifeCycles
      >,
    (location) => {
      const pathname = location.pathname;
      const techUiOwnsRoute = isTechUiRoute(pathname);
      const connectOwnsRoute = isConnectRoute(pathname);
      return techUiOwnsRoute && !connectOwnsRoute;
    },
    // Need to pass Keycloak Instance in order to not refactor the current auth flow in tech-ui
    {
      kcInstance: getKeycloakInstance(),
      getActiveWorkspaceId: (): string | null =>
        getActiveWorkspaceId(getKeycloakInstance()),
      loadParcel,
    }
  );

  return { authData };
}

export function AppInitializer() {
  use(getInitializationPromise());

  // Listen to locale changes and reload catalog
  const [currentLocale] = useLocalStorageSync({
    key: LOCALE_STORAGE_KEY,
    defaultValue: DEFAULT_LOCALE,
  });

  const locale = normalizeLocale(currentLocale);

  useEffect(() => {
    // Update HTML lang attribute for accessibility and SEO
    document.documentElement.lang = locale;

    // Keep the `locale` super property fresh on language switch so events stay
    // correctly segmented (locale is mutable mid-session, unlike the auth-derived
    // identity props set once at init).
    if (isMixpanelEnabled) {
      analyticsClient.plugins.mixpanel.setSuperProperties({ locale });
    }

    loadCatalog(locale).catch((err) => {
      console.error("Failed to load catalog:", err);
    });
  }, [locale]);

  // We need to start single-spa after the app is initialized, in order to have all the micro-frontend elements in place
  useEffectOnce(() => {
    start();
  });

  return (
    <FeatureFlagProvider>
      <AnalyticsProvider instance={analyticsClient}>
        <AuthProvider>
          <ImpersonationProvider>
            <BetaFeaturesProvider>
              <QueryClientProvider client={queryClient}>
                <App />
              </QueryClientProvider>
            </BetaFeaturesProvider>
          </ImpersonationProvider>
        </AuthProvider>
      </AnalyticsProvider>
    </FeatureFlagProvider>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary
      fallback={({ error }) => (
        <AppErrorFallback
          error={error}
          reset={() => window.location.reload()}
        />
      )}
    >
      <Suspense fallback={<FullPageLoader />}>
        <AppInitializer />
      </Suspense>
    </ErrorBoundary>
  </StrictMode>
);
